import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import type { AppointmentOperationStatus } from '../appointments/types';
import {
  adminAppointmentsApi,
  useGetAdminAppointmentOperationQuery,
} from './api/adminAppointmentsApi';

const MIN_POLL_MS = 1_000;
const MAX_POLL_MS = 30_000;

export function operationPollingInterval(
  operation?: Pick<AppointmentOperationStatus, 'status' | 'retry_after_seconds'>
): number {
  if (operation && operation.status !== 'pending') return 0;
  const requestedMs = (operation?.retry_after_seconds ?? 1) * 1_000;
  return Math.min(MAX_POLL_MS, Math.max(MIN_POLL_MS, requestedMs));
}

export function useAdminAppointmentOperation(
  operationId: string | null,
  fallbackAppointmentIds: readonly string[] = []
) {
  const dispatch = useAppDispatch();
  const invalidatedOperation = useRef<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState(MIN_POLL_MS);
  const query = useGetAdminAppointmentOperationQuery(operationId ?? skipToken, {
    pollingInterval,
    skipPollingIfUnfocused: true,
  });
  const operation = query.data;

  useEffect(() => {
    if (!operationId || !operation || operation.status === 'pending') return;
    if (invalidatedOperation.current === operationId) return;
    invalidatedOperation.current = operationId;
    const operationAppointmentIds = Array.isArray(operation.appointment_ids)
      ? operation.appointment_ids
      : [];
    const appointmentIds = Array.from(
      new Set([...operationAppointmentIds, ...fallbackAppointmentIds])
    );
    dispatch(
      adminAppointmentsApi.util.invalidateTags([
        'Appointments',
        'Sessions',
        'CalendarSettings',
        ...appointmentIds.flatMap((appointmentId) => [
          { type: 'Appointments' as const, id: appointmentId },
          { type: 'Appointments' as const, id: `${appointmentId}:history` },
        ]),
      ])
    );
  }, [dispatch, fallbackAppointmentIds, operation, operationId]);

  useEffect(() => {
    setPollingInterval(operationPollingInterval(operation));
  }, [operation]);

  return query;
}
